<template>
  <Modal
    :show="isVisible"
    :title="t('Detalhes do Webhook')"
    size="large"
    :closeLabel="t('Fechar')"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <div v-if="webhook" class="webhook-detail">
      <!-- Header Info -->
      <div class="detail-section">
        <h5>{{ t('Informações Gerais') }}</h5>
        <div class="info-grid">
          <div class="info-item">
            <label>{{ t('ID') }}</label>
            <span>{{ webhook.id }}</span>
          </div>
          <div class="info-item">
            <label>{{ t('Data') }}</label>
            <span>{{ formatDateTime(webhook.created_at) }}</span>
          </div>
          <div class="info-item">
            <label>{{ t('Provedor') }}</label>
            <span class="provider-badge">{{ webhook.provider }}</span>
          </div>
          <div class="info-item">
            <label>{{ t('Tipo de Evento') }}</label>
            <span>{{ webhook.event_type || '-' }}</span>
          </div>
          <div class="info-item">
            <label>{{ t('Status') }}</label>
            <span :class="['status-badge', getStatusClass(webhook.status)]">
              {{ getStatusLabel(webhook.status) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Error Message (if any) -->
      <div v-if="webhook.error_message" class="detail-section error-section">
        <h5>{{ t('Mensagem de Erro') }}</h5>
        <div class="error-content">
          {{ webhook.error_message }}
        </div>
      </div>

      <!-- Reprocess action -->
      <div class="detail-section reprocess-section">
        <button
          class="reprocess-btn"
          data-testid="webhook-detail-reprocess-btn"
          :disabled="reprocessing"
          @click="handleReprocess"
        >
          <i class="pi pi-refresh" />
          {{ reprocessing ? t('Reprocessando…') : t('Reprocessar') }}
        </button>
      </div>

      <!-- Payload -->
      <div class="detail-section">
        <h5>{{ t('Payload') }}</h5>
        <div class="payload-container">
          <pre class="payload-content">{{ formattedPayload }}</pre>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useDateFormatter } from '../../composables/useDateFormatter';
import { useWebhookLogs } from '../../composables/useWebhookLogs';
import Modal from '../../ui/Modal.vue';

type Id = number | string;

interface Webhook {
  id?: Id;
  created_at?: string;
  provider?: string;
  event_type?: string;
  status?: string;
  error_message?: string;
  payload?: string | Record<string, unknown>;
}

interface OpenWebhookEvent {
  webhook: Webhook;
}

const { t } = useI18n();
const { formatDateTime: formatDateTimeUtil } = useDateFormatter();
const { reprocess, reprocessing } = useWebhookLogs();

const isVisible = ref<boolean>(false);
const webhook = ref<Webhook | null>(null);

const formattedPayload = computed<string>(() => {
  if (!webhook.value?.payload) return '';

  try {
    const payload = typeof webhook.value.payload === 'string'
      ? JSON.parse(webhook.value.payload)
      : webhook.value.payload;
    return JSON.stringify(payload, null, 2);
  } catch {
    return String(webhook.value.payload);
  }
});

const formatDateTime = (dateStr: string | undefined): string => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return formatDateTimeUtil(date);
  } catch {
    return dateStr;
  }
};

const getStatusClass = (status: string | undefined): string => {
  const classMap: Record<string, string> = {
    'processed': 'status-success',
    'failed': 'status-error',
    'received': 'status-pending',
    'ignored': 'status-ignored'
  };
  return classMap[status?.toLowerCase() ?? ''] || '';
};

const getStatusLabel = (status: string | undefined): string => {
  const labelMap: Record<string, string> = {
    'processed': t('Processado'),
    'failed': t('Falhou'),
    'received': t('Recebido'),
    'ignored': t('Ignorado')
  };
  return labelMap[status?.toLowerCase() ?? ''] || status || '-';
};

const handleReprocess = async (): Promise<void> => {
  if (!webhook.value?.id) return;
  if (!confirm(t('Tem certeza que deseja reprocessar este webhook?'))) return;
  try {
    await reprocess(webhook.value.id);
    eventBus.emit('globalMessage.success', t('Webhook reprocessado com sucesso'));
    // Refresh the table so the updated status is visible
    eventBus.emit('webhook-logs.refresh');
    handleClose();
  } catch {
    eventBus.emit('globalMessage.error', t('Erro ao reprocessar webhook'));
  }
};

const handleOpen = (data?: OpenWebhookEvent): void => {
  log('WebhookDetailModal', 'handleOpen', 'Opening modal with data', data);
  if (!data) return;
  webhook.value = data.webhook;
  isVisible.value = true;
};

const handleClose = (): void => {
  log('WebhookDetailModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  webhook.value = null;
};

onMounted(() => {
  eventBus.on('modal.webhookdetail.open', handleOpen);
  eventBus.on('modal.webhookdetail.close', handleClose);
});

onBeforeUnmount(() => {
  eventBus.off('modal.webhookdetail.open', handleOpen);
  eventBus.off('modal.webhookdetail.close', handleClose);
});
</script>

<style scoped>
.webhook-detail {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.detail-section {
  background: white;
  border-radius: 8px;
  padding: 1rem;
}

.detail-section h5 {
  margin: 0 0 1rem 0;
  color: var(--p-gray-700);
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-item label {
  font-size: 0.75rem;
  color: var(--p-gray-500);
  font-weight: 500;
}

.info-item span {
  font-size: 0.875rem;
  color: var(--p-gray-800);
}

.provider-badge {
  display: inline-block;
  padding: 0.125rem 0.5rem;
  background-color: var(--p-blue-100);
  color: var(--p-blue-700);
  border-radius: 4px;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.status-success {
  background-color: var(--p-green-100);
  color: var(--p-green-700);
}

.status-error {
  background-color: var(--p-red-100);
  color: var(--p-red-700);
}

.status-pending {
  background-color: var(--p-yellow-100);
  color: var(--p-yellow-700);
}

.status-ignored {
  background-color: var(--p-gray-100);
  color: var(--p-gray-600);
}

.reprocess-section {
  display: flex;
  align-items: center;
}

.reprocess-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 1rem;
  border: 1px solid var(--p-orange-400, #fb923c);
  border-radius: 0.375rem;
  background: var(--p-orange-50, #fff7ed);
  color: var(--p-orange-700, #c2410c);
  font-size: 0.875rem;
  cursor: pointer;
  transition: background 0.15s;
}

.reprocess-btn:hover:not(:disabled) {
  background: var(--p-orange-100, #ffedd5);
}

.reprocess-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.error-section {
  border-left: 4px solid var(--p-red-500);
}

.error-content {
  background-color: var(--p-red-50);
  color: var(--p-red-700);
  padding: 0.75rem;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.875rem;
  white-space: pre-wrap;
  word-break: break-word;
}

.payload-container {
  max-height: 400px;
  overflow: auto;
  background-color: var(--p-gray-900);
  border-radius: 4px;
}

.payload-content {
  margin: 0;
  padding: 1rem;
  color: var(--p-green-400);
  font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
  font-size: 0.75rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
